import { useMemo, useRef } from "react";
import type { ChangeEvent } from "react";
import SmartImage from "@/pages/seller/dashboard/components/SmartImage";
import { useToast } from "@/components/feature/Toast";
import { MAX_UPLOAD_BYTES, formatFileSize } from "@/lib/media";

interface ImageUploaderProps {
  files: File[];
  existingUrls: string[];
  max?: number;
  onFilesChange: (files: File[]) => void;
  onRemoveExisting: (index: number) => void;
}

const ACCEPT = "image/*";

export default function ImageUploader({
  files,
  existingUrls,
  max = 8,
  onFilesChange,
  onRemoveExisting,
}: ImageUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToast();
  const total = files.length + existingUrls.length;

  const previews = useMemo(
    () => files.map((file) => URL.createObjectURL(file)),
    [files]
  );

  const handleSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    const remaining = Math.max(max - total, 0);
    const accepted: File[] = [];

    for (const file of selected) {
      if (!file.type.startsWith("image/")) {
        showToast(`Solo se permiten imágenes: ${file.name}`, "error");
        continue;
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        showToast(
          `«${file.name}» supera el límite de 5 MB (${formatFileSize(
            file.size
          )}).`,
          "error"
        );
        continue;
      }
      if (accepted.length >= remaining) break;
      accepted.push(file);
    }

    if (accepted.length > 0) onFilesChange([...files, ...accepted]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeFile = (index: number) => {
    onFilesChange(files.filter((_, i) => i !== index));
  };

  return (
    <div>
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
        {existingUrls.map((url, i) => (
          <div
            key={`existing-${i}`}
            className="group relative aspect-square overflow-hidden rounded-lg border border-background-200 bg-background-100"
          >
            <SmartImage
              src={url}
              alt={`Imagen ${i + 1}`}
              className="h-full w-full object-cover object-top"
            />
            <button
              type="button"
              onClick={() => onRemoveExisting(i)}
              aria-label="Eliminar imagen"
              className="absolute right-1.5 top-1.5 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-foreground-950/60 text-white transition-colors hover:bg-accent-600"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        ))}

        {files.map((file, i) => (
          <div
            key={`new-${i}`}
            className="group relative aspect-square overflow-hidden rounded-lg border border-background-200 bg-background-100"
          >
            <img
              src={previews[i]}
              alt={`Vista previa ${i + 1}`}
              className="h-full w-full object-cover object-top"
            />
            <button
              type="button"
              onClick={() => removeFile(i)}
              aria-label="Eliminar archivo"
              className="absolute right-1.5 top-1.5 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-foreground-950/60 text-white transition-colors hover:bg-accent-600"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        ))}

        {total < max && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex aspect-square cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-background-300 text-foreground-400 transition-colors hover:border-primary-400 hover:text-primary-500"
          >
            <i className="ri-add-line text-2xl"></i>
            <span className="text-xs font-medium">Agregar foto</span>
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple
        className="hidden"
        onChange={handleSelect}
      />

      <p className="mt-2 text-xs text-foreground-500">
        {total}/{max} fotos · JPG, PNG o WEBP · máx. 5 MB cada una · La primera
        será la portada.
      </p>
    </div>
  );
}